<template>
  <div>
    <div class="se-detail-section">
      <div class="se-detail-h">Properties</div>
      <div class="se-kv"><span class="k">Type</span><span class="v">{{ stage.stageType }}</span></div>
      <div class="se-kv"><span class="k">Media</span><span class="v">{{ stage.mediaType }}</span></div>
      <div v-if="stage.inputType" class="se-kv"><span class="k">Input</span><span class="v">{{ stage.inputType }}</span></div>
      <div v-if="stage.endsChat" class="se-kv"><span class="k">Ends chat</span><span class="v">Yes</span></div>
    </div>

    <div class="se-detail-section">
      <div class="se-detail-h">Topic mapping <span class="se-ai-chip">✨ AI generated</span></div>
      <p class="se-hint">Phrases the NLP model matches against user questions to route them into this stage.</p>
      <div class="se-tag-chips">
        <span v-for="(t, i) in stage.topics" :key="i" class="se-tag-chip">
          {{ t }}<button class="se-tag-x" title="Remove" @click="removeTopic(i)">×</button>
        </span>
      </div>
      <div class="se-tag-add-row">
        <input v-model="topicInput" class="se-tag-input" placeholder="Add a phrase or question..." @keydown.enter.prevent="addTopic" />
        <button class="se-mini-btn" @click="addTopic">+ Add</button>
        <button class="se-mini-btn" @click="generateTopics">✨ Generate</button>
      </div>
    </div>

    <div v-if="stage.availableLangs && stage.availableLangs.length" class="se-detail-section">
      <div class="se-detail-h">Message</div>
      <div class="se-lang-chips">
        <button
          v-for="l in stage.availableLangs"
          :key="l"
          class="se-lang-chip"
          :class="{ active: detailLang === l }"
          @click="detailLang = l"
        >{{ langLabel(l) }}</button>
      </div>
      <textarea
        class="se-msg-box se-msg-edit"
        rows="6"
        v-model="messageDraft"
        @blur="commitMessage"
      ></textarea>
    </div>

    <div v-if="stage.options" class="se-detail-section">
      <div class="se-detail-h">Options → next</div>
      <table class="se-opt-table">
        <thead><tr><th>Option</th><th>Next stage</th></tr></thead>
        <tbody>
          <tr v-for="(o, i) in stage.options" :key="i">
            <td><span class="se-opt-slug">{{ o.opt }}</span></td>
            <td>
              <div class="se-next-row">
                <nv-select class="se-next-select" :value="o.next || ''">
                  <option value="">— choose —</option>
                  <option v-for="name in stageNames" :key="name" :value="name">{{ name }}</option>
                </nv-select>
                <button v-if="o.next" class="se-jump-btn" title="Open this stage" @click="$emit('select-stage', o.next)">↗</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="stage.next" class="se-detail-section">
      <div class="se-detail-h">Next</div>
      <div class="se-next-row">
        <nv-select class="se-next-select" :value="stage.next">
          <option v-for="name in stageNames" :key="name" :value="name">{{ name }}</option>
        </nv-select>
        <button class="se-jump-btn" title="Open this stage" @click="$emit('select-stage', stage.next)">↗</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'stage-content-tab',
  props: {
    stage: { type: Object, required: true },
    langOptions: { type: Array, default: () => [] },
    currentLang: { type: String, default: '' },
    allStages: { type: Array, default: () => [] }
  },
  data () {
    return {
      detailLang: '',
      messageDraft: '',
      topicInput: ''
    }
  },
  computed: {
    stageNames () {
      return this.allStages.map(s => s.name)
    }
  },
  watch: {
    stage: {
      immediate: true,
      handler (val) {
        if (!val.availableLangs) return
        this.detailLang = val.availableLangs.includes(this.currentLang) ? this.currentLang : (val.availableLangs[0] || '')
        this.messageDraft = val.messages[this.detailLang] || ''
      }
    },
    detailLang (val) {
      this.messageDraft = this.stage.messages[val] || ''
    }
  },
  methods: {
    langLabel (code) {
      const found = this.langOptions.find(l => l.code === code)
      return found ? found.label : code
    },
    commitMessage () {
      if (this.messageDraft === (this.stage.messages[this.detailLang] || '')) return
      this.$emit('update-message', { stageName: this.stage.name, lang: this.detailLang, text: this.messageDraft })
    },
    addTopic () {
      const val = this.topicInput.trim()
      if (!val) return
      this.topicInput = ''
      this.$emit('update-topics', { stageName: this.stage.name, topics: [...(this.stage.topics || []), val] })
    },
    removeTopic (i) {
      const topics = this.stage.topics.slice()
      topics.splice(i, 1)
      this.$emit('update-topics', { stageName: this.stage.name, topics })
    },
    generateTopics () {
      const base = this.stage.name.replace(/_/g, ' ')
      const suggestions = [`questions about ${base}`, 'what does this mean for me', 'is this true for my situation']
      const existing = new Set(this.stage.topics || [])
      const fresh = suggestions.filter(s => !existing.has(s))
      this.$emit('update-topics', { stageName: this.stage.name, topics: [...(this.stage.topics || []), ...fresh] })
    }
  }
}
</script>

<style scoped>
.se-detail-section{margin-top:16px}
.se-detail-section:first-child{margin-top:0}
.se-detail-h{font-size:10.5px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;display:flex;align-items:center;gap:6px}
.se-hint{font-size:11px;color:var(--text-faint);margin:0 0 8px;line-height:1.5}
.se-ai-chip{
  font-size:9px;font-weight:800;letter-spacing:0.04em;background:linear-gradient(135deg,#8B5CF6,#6D28D9);
  color:#fff;padding:1px 7px;border-radius:999px;text-transform:none;
}
.se-kv{display:flex;gap:8px;padding:3px 0;font-size:12px;align-items:baseline}
.se-kv .k{color:var(--text-muted);min-width:70px}
.se-kv .v{font-family:'JetBrains Mono',monospace;color:var(--text)}
.se-tag-chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px}
.se-tag-chip{
  display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;
  padding:4px 6px 4px 10px;border-radius:999px;border:1px solid rgba(139,92,246,0.25);
  background:rgba(139,92,246,0.1);color:#6D28D9;
}
.se-tag-x{border:none;background:none;cursor:pointer;font-size:13px;line-height:1;padding:0 2px;color:inherit;opacity:0.75}
.se-tag-x:hover{opacity:1}
.se-tag-add-row{display:flex;gap:6px}
.se-tag-input{flex:1;padding:6px 9px;border:1px solid var(--card-border);border-radius:7px;font-size:12px;background:var(--card);color:var(--text)}
.se-tag-input:focus{outline:none;border-color:var(--accent)}
.se-mini-btn{
  font-size:10.8px;font-weight:700;padding:4px 9px;border-radius:6px;
  background:var(--card);border:1px solid var(--card-border);color:var(--text-muted);cursor:pointer;white-space:nowrap;
}
.se-mini-btn:hover{background:var(--card-label-bg);color:var(--text)}

.se-lang-chips{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:8px}
.se-lang-chip{
  padding:3px 10px;border-radius:999px;border:1px solid var(--card-border);
  background:var(--card);color:var(--text-muted);font-size:11px;font-weight:500;
  cursor:pointer;font-family:inherit;transition:all .12s;
}
.se-lang-chip:hover{border-color:var(--btn-hl-border);color:var(--text)}
.se-lang-chip.active{border-color:var(--accent);color:var(--accent-light);background:rgba(13,148,136,0.12)}
.se-msg-box{
  background:var(--card-label-bg);border:1px solid var(--card-border);border-radius:7px;
  padding:10px 11px;font-size:12px;white-space:pre-wrap;word-break:break-word;line-height:1.55;color:var(--text);
}
.se-msg-edit{width:100%;resize:vertical;font-family:inherit;}
.se-msg-edit:focus{outline:none;border-color:var(--accent)}
.se-opt-table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:4px;background:var(--card);border-radius:6px;overflow:hidden;border:1px solid var(--card-border)}
.se-opt-table th,.se-opt-table td{padding:6px 8px;border-bottom:1px solid var(--card-border);vertical-align:top;text-align:left}
.se-opt-table tr:last-child td{border-bottom:none}
.se-opt-table th{font-size:9.5px;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);background:var(--card-label-bg);font-weight:600}
.se-opt-slug{font-family:'JetBrains Mono',monospace;background:var(--card-label-bg);padding:2px 6px;border-radius:3px;font-size:10.5px;color:var(--text)}
.se-next-row{display:flex;align-items:center;gap:5px}
.se-next-select{
  flex:1;font-family:'JetBrains Mono',monospace;font-size:11px;border:1px solid var(--card-border);
  border-radius:6px;padding:3px 5px;background:var(--card);color:var(--text);max-width:160px;
}
.se-next-select:focus{outline:none;border-color:var(--accent)}
.se-jump-btn{
  width:20px;height:20px;border-radius:5px;border:1px solid var(--card-border);background:var(--card);
  color:var(--accent-light);cursor:pointer;font-size:11px;flex-shrink:0;
}
.se-jump-btn:hover{background:var(--card-label-bg)}
</style>



// WEBPACK FOOTER //
// StageContentTab.vue?bf9ce0a0